// v77.66 — Landing page redesign (mobile-first, calm, structured, trust-led).
//
// Spec source: full user-supplied redesign brief.  Sections, copy and tone
// follow that brief exactly. Goal: clarity, credibility, conversion.
//
// Visual direction:
//   • Soft teal-on-white gradient (light) / dark-teal-on-charcoal (dark).
//   • Card-based blocks with crisp borders, subtle shadows.
//   • Generous spacing, short line-lengths, no filler copy.
//   • Real app screenshot inside a phone mockup as the hero focal point.
//
// Mobile-first: every layout uses a single column on small screens,
// progressively widening to 2/3-col grids at md+/lg+.
//
// All interactive elements carry a `data-testid` for regression coverage.

import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import LegalFooterInfo from "@/components/LegalFooterInfo";
import { trackEvent } from "@/lib/analytics";
import {
  MONTHLY_PRICE_EUR,
  YEARLY_PRICE_EUR,
  YEARLY_SAVINGS_EUR,
} from "@/lib/pricing";
import {
  Calendar,
  ReceiptEuro,
  MessageSquare,
  BookOpen,
  Users,
  ArrowLeftRight,
  Wallet,
  ShieldCheck,
  ScrollText,
  FileLock2,
  Eye,
  Lock,
  GitBranch,
  ArrowRight,
  CircleDot,
  HeartHandshake,
  Menu,
  X,
  Fingerprint,
  QrCode,
  CheckCircle2,
  Star,
  Quote,
  HandHelping,
} from "lucide-react";

import { useState, useEffect } from "react";

// v77.82 — Hero phone mockup is now built from native React components
// (Dashboard / Calendar / Expenses) that auto-cycle every 4 seconds
// rather than a baked-in browser screenshot. This kills the URL-bar
// problem entirely (no Chrome screenshot, no chrome-native:// pixels
// to clip) AND showcases more of the product without the visitor
// having to scroll.
const HERO_SCREENS = ["dashboard", "calendar", "expenses"];
const HERO_CYCLE_MS = 4000;

// ---------- Reusable bits ---------------------------------------------------

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1F9FA4] dark:text-[#3FB8BC]">
    {children}
  </div>
);

const SectionHeading = ({ eyebrow, title, intro, center = false }) => (
  <div className={center ? "max-w-2xl mx-auto text-center" : "max-w-2xl"}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
      {title}
    </h2>
    {intro && (
      <p className="text-base leading-relaxed mt-4 text-[#48595E] dark:text-[#A8B6BB]">
        {intro}
      </p>
    )}
  </div>
);

// Card surface used by feature pillars / situation rows / etc. Subtle border,
// soft shadow on hover; dark-mode pair is a slate slightly lifted from the
// page bg so cards still read as "raised".
const cardSurface =
  "border border-[#E5EBEC] dark:border-[#2A3539] " +
  "bg-white dark:bg-[#161E21] " +
  "rounded-xl shadow-[0_1px_2px_rgba(15,21,23,0.04)] " +
  "hover:shadow-[0_8px_24px_-12px_rgba(31,159,164,0.18)] " +
  "transition-shadow duration-200";

const FeatureCard = ({ icon: Icon, title, desc, testid }) => (
  <div className={`${cardSurface} p-6`} data-testid={testid}>
    <div className="w-11 h-11 rounded-lg bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 inline-flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.6} />
    </div>
    <h3 className="font-heading text-lg font-semibold mt-5 text-[#1A2225] dark:text-[#F4F8F9]">
      {title}
    </h3>
    <p className="text-sm leading-relaxed mt-2 text-[#48595E] dark:text-[#A8B6BB]">
      {desc}
    </p>
  </div>
);

const SituationRow = ({ icon: Icon, title, desc, testid }) => (
  <div className={`${cardSurface} p-5 flex gap-4`} data-testid={testid}>
    <div className="w-10 h-10 rounded-lg bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 inline-flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.6} />
    </div>
    <div className="min-w-0">
      <div className="font-heading text-base font-semibold text-[#1A2225] dark:text-[#F4F8F9]">
        {title}
      </div>
      <div className="text-sm leading-relaxed mt-1 text-[#48595E] dark:text-[#A8B6BB]">
        {desc}
      </div>
    </div>
  </div>
);

const TrustBadge = ({ icon: Icon, label, testid }) => (
  <div
    className="flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white/70 dark:bg-[#161E21]/70 border border-[#D6F0EF] dark:border-[#1F9FA4]/30 backdrop-blur"
    data-testid={testid}
  >
    <Icon className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.8} />
    <span className="text-sm font-medium text-[#1A2225] dark:text-[#E8EEF0]">{label}</span>
  </div>
);

const StepCard = ({ num, icon: Icon, title, desc, testid }) => (
  <div
    className={`${cardSurface} p-6 flex gap-4 items-start`}
    data-testid={testid}
  >
    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#116B6E] text-white inline-flex items-center justify-center font-heading font-semibold text-sm">
      {String(num).padStart(2, "0")}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.8} />
        <h3 className="font-heading text-base font-semibold text-[#1A2225] dark:text-[#F4F8F9]">
          {title}
        </h3>
      </div>
      <p className="text-sm leading-relaxed mt-2 text-[#48595E] dark:text-[#A8B6BB]">
        {desc}
      </p>
    </div>
  </div>
);

// ---------- Hero phone mockup screens --------------------------------------
//
// Three native React mini-screens that fit inside the iPhone-style bezel
// (260–300px wide). Each one is a lightweight, on-brand reproduction of a
// real in-app view — not pixel-perfect, but unmistakably the same product.
// Built native (no screenshots) so:
//   • there's no browser URL bar to clip,
//   • they stay sharp at any density,
//   • they cycle without a network refetch.
// All three share the same outer chrome (status bar + brand header + bottom
// nav) so cycling feels like switching tabs, not loading a new app.

const HeroStatusBar = () => (
  <div className="flex items-center justify-between px-5 pt-3 pb-1 text-[10px] font-medium text-[#1A2225] dark:text-[#E8EEF0]">
    <span className="font-mono-data">9:41</span>
    <div className="flex items-center gap-1">
      <span className="w-3 h-1.5 rounded-[1px] bg-[#1A2225] dark:bg-[#E8EEF0]" />
      <span className="w-3 h-1.5 rounded-[1px] bg-[#1A2225] dark:bg-[#E8EEF0]" />
      <span className="w-4 h-1.5 rounded-[1px] bg-[#1A2225] dark:bg-[#E8EEF0]" />
    </div>
  </div>
);

const HeroAppHeader = ({ title, sub }) => (
  <div className="px-5 pt-3 pb-2">
    <div className="text-[9px] uppercase tracking-[0.18em] font-semibold text-[#1F9FA4]">
      {sub}
    </div>
    <div className="font-heading text-[18px] leading-tight font-semibold text-[#1A2225] dark:text-[#F4F8F9] mt-0.5">
      {title}
    </div>
  </div>
);

const HeroBottomNav = ({ active }) => {
  const tabs = [
    { key: "dashboard", label: "Home", Icon: Wallet },
    { key: "calendar", label: "Calendar", Icon: Calendar },
    { key: "expenses", label: "Expenses", Icon: ReceiptEuro },
    { key: "messages", label: "Messages", Icon: MessageSquare },
  ];
  return (
    <div className="absolute bottom-0 inset-x-0 border-t border-[#E5EBEC] dark:border-[#1F2A2D] bg-white/95 dark:bg-[#0E1416]/95 backdrop-blur-sm flex items-center justify-around py-2">
      {tabs.map(({ key, label, Icon }) => (
        <div
          key={key}
          className={`flex flex-col items-center gap-0.5 ${
            active === key
              ? "text-[#116B6E] dark:text-[#3FB8BC]"
              : "text-[#9AA8AD] dark:text-[#5C6B70]"
          }`}
        >
          <Icon className="w-4 h-4" strokeWidth={1.8} />
          <span className="text-[8px] font-medium">{label}</span>
        </div>
      ))}
    </div>
  );
};

const DashboardScreen = () => (
  <>
    <HeroStatusBar />
    <HeroAppHeader sub="Overview" title="Hello, Alex" />
    <div className="px-4 pt-1 pb-20 space-y-2.5">
      <div className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#F4FAFA] dark:bg-[#0F2F30]/30 px-3 py-2.5">
        <div className="text-[9px] uppercase tracking-[0.14em] text-[#6F828A]">Today, 29 April</div>
        <div className="text-[11px] font-medium text-[#1A2225] dark:text-[#F4F8F9] mt-1">
          You have <span className="text-[#116B6E] dark:text-[#3FB8BC]">Mia</span> &amp; <span className="text-[#116B6E] dark:text-[#3FB8BC]">Noah</span>
        </div>
      </div>
      {[
        { color: "#1F9FA4", name: "Mia", note: "Handover · Fri 20:00" },
        { color: "#88A47C", name: "Noah", note: "With you · all week" },
        { color: "#D7714C", name: "E",    note: "Handover · Fri 17:00" },
      ].map((c) => (
        <div key={c.name} className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-3 py-2 flex items-center gap-2.5">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-medium text-[#1A2225] dark:text-[#F4F8F9]">{c.name}</div>
            <div className="text-[9px] text-[#6F828A] mt-0.5">{c.note}</div>
          </div>
        </div>
      ))}
      <div className="grid grid-cols-2 gap-2 pt-1">
        <div className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-2.5 py-2">
          <div className="text-[8px] uppercase tracking-[0.14em] text-[#6F828A]">Unread</div>
          <div className="font-heading text-base font-semibold text-[#1A2225] dark:text-[#F4F8F9]">0</div>
        </div>
        <div className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-2.5 py-2">
          <div className="text-[8px] uppercase tracking-[0.14em] text-[#6F828A]">Outstanding</div>
          <div className="font-heading text-base font-semibold text-[#C0291D]">€204</div>
        </div>
      </div>
    </div>
    <HeroBottomNav active="dashboard" />
  </>
);

const CalendarScreen = () => {
  // April 2026 starts on a Wednesday (weekday index 3 — Mon-first grid).
  const events = { 4: "#1F9FA4", 9: "#88A47C", 12: "#D7714C", 18: "#1F9FA4", 23: "#88A47C", 26: "#D7714C", 29: "#1F9FA4" };
  const cells = [];
  for (let i = 0; i < 3; i++) cells.push(null);            // pre-roll
  for (let d = 1; d <= 30; d++) cells.push(d);              // April has 30 days
  while (cells.length % 7 !== 0) cells.push(null);          // trailing pad
  return (
    <>
      <HeroStatusBar />
      <HeroAppHeader sub="April 2026" title="Schedule" />
      <div className="px-4 pt-1 pb-20">
        <div className="grid grid-cols-7 gap-1 text-[8px] text-center uppercase tracking-[0.1em] text-[#6F828A] mb-1">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d}>{d.charAt(0)}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => (
            // Stable cell key: "blank-<grid-index>" for leading blanks,
            // "day-<n>" for actual day cells. Indices are fine here
            // because the cells grid never reorders — but a labelled
            // key is clearer and silences the lint warning.
            <div key={d == null ? `blank-${i}` : `day-${d}`} className="aspect-square rounded-sm flex flex-col items-center justify-center bg-white dark:bg-[#0E1416] border border-[#EEF3F4] dark:border-[#1F2A2D] relative">
              {d && (
                <>
                  <span className={`text-[9px] font-medium ${d === 29 ? "text-white" : "text-[#1A2225] dark:text-[#F4F8F9]"}`}>{d}</span>
                  {d === 29 && <span aria-hidden="true" className="absolute inset-0 -z-10 rounded-sm bg-[#116B6E]" />}
                  {events[d] && d !== 29 && (
                    <span className="w-1 h-1 rounded-full mt-0.5" style={{ backgroundColor: events[d] }} />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-1.5">
          <div className="text-[8px] uppercase tracking-[0.14em] font-semibold text-[#6F828A]">Today · 29 Apr</div>
          <div className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-2.5 py-2 flex items-center gap-2">
            <span className="w-1 h-6 rounded-full bg-[#1F9FA4]" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-medium text-[#1A2225] dark:text-[#F4F8F9]">School pickup · Mia</div>
              <div className="text-[8px] text-[#6F828A]">15:30 · Sam Parent</div>
            </div>
          </div>
          <div className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-2.5 py-2 flex items-center gap-2">
            <span className="w-1 h-6 rounded-full bg-[#88A47C]" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-medium text-[#1A2225] dark:text-[#F4F8F9]">Football practice · Noah</div>
              <div className="text-[8px] text-[#6F828A]">17:00 · Alex</div>
            </div>
          </div>
        </div>
      </div>
      <HeroBottomNav active="calendar" />
    </>
  );
};

const ExpensesScreen = () => (
  <>
    <HeroStatusBar />
    <HeroAppHeader sub="Shared · EUR" title="Expenses" />
    <div className="px-4 pt-1 pb-20 space-y-2.5">
      <div className="rounded-md bg-[#F4FAFA] dark:bg-[#0F2F30]/30 border border-[#D6F0EF] dark:border-[#1F9FA4]/30 px-3 py-2.5">
        <div className="text-[9px] uppercase tracking-[0.14em] font-semibold text-[#116B6E] dark:text-[#3FB8BC]">Net settlement</div>
        <div className="flex items-baseline justify-between mt-1">
          <div className="text-[11px] text-[#1A2225] dark:text-[#F4F8F9]">
            <span className="font-semibold">Sam</span> owes <span className="font-semibold">you</span>
          </div>
          <div className="font-heading font-semibold text-[#C0291D] text-base">€204.00</div>
        </div>
      </div>
      {[
        { label: "Tutor", amt: "€80.00", state: "Settled", color: "ok" },
        { label: "School trip", amt: "€42.00", state: "Outstanding", color: "warn" },
        { label: "Optician", amt: "€60.00", state: "Settled", color: "ok" },
        { label: "Football kit", amt: "€22.00", state: "Outstanding", color: "warn" },
      ].map((e) => (
        <div key={e.label} className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <CircleDot className="w-3 h-3 text-[#1F9FA4] flex-shrink-0" />
            <span className="text-[10px] font-medium text-[#1A2225] dark:text-[#F4F8F9] truncate">{e.label}</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="font-mono-data text-[9px] text-[#48595E] dark:text-[#A8B6BB]">{e.amt}</span>
            <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${
              e.color === "ok"
                ? "bg-[#E0F2F2] text-[#116B6E] dark:bg-[#1F9FA4]/15 dark:text-[#3FB8BC]"
                : "bg-[#FFF1E5] text-[#A04E1B] dark:bg-[#3A2418] dark:text-[#FFB680]"
            }`}>{e.state}</span>
          </div>
        </div>
      ))}
    </div>
    <HeroBottomNav active="expenses" />
  </>
);

const HERO_SCREEN_RENDERERS = {
  dashboard: <DashboardScreen />,
  calendar: <CalendarScreen />,
  expenses: <ExpensesScreen />,
};

// ---------- Page -----------------------------------------------------------

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  // v77.82 — Hero phone mockup auto-cycles through Dashboard / Calendar /
  // Expenses screens every 4s. Pauses while the document is hidden so we
  // don't waste timers in background tabs, and respects the user's
  // `prefers-reduced-motion` setting (locked to dashboard).
  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduce) return;
    const id = window.setInterval(() => {
      if (document.hidden) return;
      setHeroIdx((i) => (i + 1) % HERO_SCREENS.length);
    }, HERO_CYCLE_MS);
    return () => window.clearInterval(id);
  }, []);
  const heroScreen = HERO_SCREENS[heroIdx];

  return (
    <div
      className="min-h-screen bg-white dark:bg-[#0E1416] text-[#1A2225] dark:text-[#E8EEF0]"
      data-testid="landing-page"
    >
      {/* ------------------------------ HEADER ----------------------------- */}
      <header
        className="sticky top-0 z-30 border-b border-[#E5EBEC] dark:border-[#1F2A2D] bg-white/85 dark:bg-[#0E1416]/85 backdrop-blur"
        data-testid="landing-header"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
            <Logo size={32} />
            <span className="font-heading font-semibold text-base sm:text-lg tracking-tight text-[#1A2225] dark:text-[#F4F8F9]">
              CoParent Console
            </span>
          </Link>

          {/* Desktop nav — inline About link + Sign in / Get started CTAs. */}
          <div className="hidden sm:flex items-center gap-1.5 sm:gap-2">
            <Link to="/about" data-testid="nav-about-link-desktop">
              <Button
                variant="ghost"
                size="sm"
                className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] hover:bg-[#E0F2F2] dark:hover:bg-[#1F9FA4]/15"
              >
                About
              </Button>
            </Link>
            <Link to="/professionals" data-testid="nav-professionals-link-desktop">
              <Button
                variant="ghost"
                size="sm"
                className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] hover:bg-[#E0F2F2] dark:hover:bg-[#1F9FA4]/15"
              >
                For professionals
              </Button>
            </Link>
            <Link to="/pricing" data-testid="nav-pricing-link-desktop">
              <Button
                variant="ghost"
                size="sm"
                className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] hover:bg-[#E0F2F2] dark:hover:bg-[#1F9FA4]/15"
              >
                Pricing
              </Button>
            </Link>
            <Link to="/help" data-testid="nav-help-link-desktop">
              <Button
                variant="ghost"
                size="sm"
                className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] hover:bg-[#E0F2F2] dark:hover:bg-[#1F9FA4]/15"
              >
                Help
              </Button>
            </Link>
            <Link to="/contact" data-testid="nav-contact-link-desktop">
              <Button
                variant="ghost"
                size="sm"
                className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] hover:bg-[#E0F2F2] dark:hover:bg-[#1F9FA4]/15"
              >
                Contact
              </Button>
            </Link>
            <Link to="/auth" data-testid="nav-login-link">
              <Button
                variant="ghost"
                size="sm"
                className="bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 text-[#116B6E] dark:text-[#3FB8BC] hover:bg-[#C7E7E8] dark:hover:bg-[#1F9FA4]/25"
              >
                Sign in
              </Button>
            </Link>
            <Link to="/auth?mode=register" data-testid="nav-register-link">
              <Button
                size="sm"
                className="bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md"
              >
                Get started
              </Button>
            </Link>
          </div>

          {/* Mobile nav — primary "Get started" stays visible; hamburger
              opens an inline menu containing About + Sign in. Keeps the
              top-of-funnel CTA one tap away while satisfying the spec
              that "About" lives in the hamburger. */}
          <div className="flex sm:hidden items-center gap-1.5">
            <Link to="/auth?mode=register" data-testid="nav-register-link-mobile">
              <Button
                size="sm"
                className="bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-9 px-3 text-sm"
              >
                Get started
              </Button>
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="landing-mobile-menu"
              className="w-10 h-10 inline-flex items-center justify-center rounded-md text-[#1A2225] dark:text-[#F4F8F9] hover:bg-[#E0F2F2] dark:hover:bg-[#1F9FA4]/15 transition-colors"
              data-testid="landing-menu-toggle"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu — collapsible, hides on link click. */}
        {menuOpen && (
          <div
            id="landing-mobile-menu"
            className="sm:hidden border-t border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] origin-top animate-in fade-in slide-in-from-top-2 duration-200 ease-out"
            data-testid="landing-mobile-menu"
          >
            <div className="max-w-6xl mx-auto px-4 py-3 flex flex-col">
              <Link
                to="/about"
                onClick={() => setMenuOpen(false)}
                className="px-2 py-3 text-base font-medium text-[#1A2225] dark:text-[#F4F8F9] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] border-b border-[#E5EBEC] dark:border-[#1F2A2D]"
                data-testid="nav-about-link-mobile"
              >
                About
              </Link>
              <Link
                to="/professionals"
                onClick={() => setMenuOpen(false)}
                className="px-2 py-3 text-base font-medium text-[#1A2225] dark:text-[#F4F8F9] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] border-b border-[#E5EBEC] dark:border-[#1F2A2D]"
                data-testid="nav-professionals-link-mobile"
              >
                For professionals
              </Link>
              <Link
                to="/pricing"
                onClick={() => setMenuOpen(false)}
                className="px-2 py-3 text-base font-medium text-[#1A2225] dark:text-[#F4F8F9] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] border-b border-[#E5EBEC] dark:border-[#1F2A2D]"
                data-testid="nav-pricing-link-mobile"
              >
                Pricing
              </Link>
              <Link
                to="/help"
                onClick={() => setMenuOpen(false)}
                className="px-2 py-3 text-base font-medium text-[#1A2225] dark:text-[#F4F8F9] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] border-b border-[#E5EBEC] dark:border-[#1F2A2D]"
                data-testid="nav-help-link-mobile"
              >
                Help
              </Link>
              <Link
                to="/contact"
                onClick={() => setMenuOpen(false)}
                className="px-2 py-3 text-base font-medium text-[#1A2225] dark:text-[#F4F8F9] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] border-b border-[#E5EBEC] dark:border-[#1F2A2D]"
                data-testid="nav-contact-link-mobile"
              >
                Contact
              </Link>
              <Link
                to="/auth"
                onClick={() => setMenuOpen(false)}
                className="mt-2 px-3 py-3 rounded-md text-base font-medium text-center bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 text-[#116B6E] dark:text-[#3FB8BC] hover:bg-[#C7E7E8] dark:hover:bg-[#1F9FA4]/25 transition-colors"
                data-testid="nav-login-link-mobile"
              >
                Sign in
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* ------------------------------ 1. HERO ---------------------------- */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* Soft teal-to-white gradient + faint radial accent. Sits behind
            content — pointer-events:none so nothing under it reacts to clicks. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#E0F2F2]/60 via-white to-white dark:from-[#0F2F30]/70 dark:via-[#0E1416] dark:to-[#0E1416]"
        />
        <div
          aria-hidden="true"
          className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#1F9FA4]/10 dark:bg-[#1F9FA4]/15 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-24 pb-16 sm:pb-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <div className="lg:col-span-7 text-center lg:text-left">
              <h1 className="font-heading text-[2rem] leading-[1.1] sm:text-5xl lg:text-[3.5rem] lg:leading-[1.05] font-semibold tracking-tight text-[#1A2225] dark:text-[#F4F8F9]">
                Co-parenting, organised.
                <br />
                <span className="text-[#116B6E] dark:text-[#3FB8BC]">
                  Calm, clear, accountable.
                </span>
              </h1>
              <p className="text-base sm:text-lg leading-relaxed mt-5 sm:mt-6 max-w-xl mx-auto lg:mx-0 text-[#48595E] dark:text-[#B6C2C7]">
                A structured, secure place to manage schedules, contact visits,
                handovers, expenses, communication and records. Every event
                produces a neutral, time-stamped record &mdash; calm by design,
                secure by default.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-7 sm:mt-8 justify-center lg:justify-start">
                <Link
                  to="/auth?mode=register"
                  data-testid="hero-cta-primary"
                  className="w-full sm:w-auto"
                  onClick={() => trackEvent("cta_start_free_trial", { location: "landing_hero" })}
                >
                  <Button className="w-full sm:w-auto bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-12 px-7 text-base font-medium shadow-sm transition-colors">
                    Start free trial
                    <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
                  </Button>
                </Link>
                <a
                  href="#features"
                  data-testid="hero-cta-secondary"
                  className="w-full sm:w-auto"
                >
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto h-12 px-7 text-base rounded-md border-[#D6F0EF] dark:border-[#2A3539] bg-white/60 dark:bg-[#161E21] text-[#1A2225] dark:text-[#E8EEF0] hover:border-[#116B6E] dark:hover:border-[#3FB8BC] hover:text-[#116B6E] dark:hover:text-[#3FB8BC]"
                  >
                    Learn more
                  </Button>
                </a>
              </div>
              {/* Quiet pricing reassurance — the user already knows the
                  ballpark before clicking through to /auth or /pricing,
                  which removes a major source of register-screen drop-off.
                  Lead with the FREE-TRIAL line because that's the single
                  biggest objection at the register screen ("I don't want
                  to enter card details to look around"). The price + min
                  commitment sit on a calmer second line so users see the
                  full picture without it feeling like fine print. */}
              <div
                className="mt-5 flex flex-col items-center lg:items-start gap-2"
                data-testid="hero-pricing-chip"
              >
                <Link
                  to="/auth?mode=register"
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 border border-[#1F9FA4]/40 dark:border-[#1F9FA4]/30 text-xs sm:text-sm font-medium text-[#0B4D50] dark:text-[#3FB8BC] hover:bg-[#CCEAEA] dark:hover:bg-[#1F9FA4]/25 transition-colors"
                  data-testid="hero-trial-chip"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.4} />
                  <span>30-day free trial</span>
                  <span className="text-[#0B4D50]/70 dark:text-[#3FB8BC]/80" aria-hidden="true">·</span>
                  <span>No card required</span>
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex flex-wrap items-center justify-center lg:justify-start gap-x-2.5 gap-y-1 text-xs sm:text-sm text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] transition-colors"
                  data-testid="hero-pricing-line"
                >
                  <span>Then </span>
                  <span className="font-mono-data tabular-nums font-semibold text-[#1A2225] dark:text-[#F4F8F9]" data-testid="hero-monthly-price">€{MONTHLY_PRICE_EUR.toFixed(2)}</span>
                  <span>/ month</span>
                  <span aria-hidden="true" className="text-[#C5CDD0] dark:text-[#2A3539]">·</span>
                  <span>or</span>
                  <span className="font-mono-data tabular-nums font-semibold text-[#1A2225] dark:text-[#F4F8F9]" data-testid="hero-yearly-price">€{YEARLY_PRICE_EUR.toFixed(0)}</span>
                  <span>/ year</span>
                  <span aria-hidden="true" className="text-[#C5CDD0] dark:text-[#2A3539]">·</span>
                  {/* v77.99.10 — surface the savings as the dominant
                      visual cue. The strong colour pulls the eye to
                      the most converting line on the page. */}
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-[#E0F2F2] dark:bg-[#1F9FA4]/20 text-[#0B4D50] dark:text-[#3FB8BC] font-semibold text-[11px] sm:text-xs"
                    data-testid="hero-yearly-savings"
                  >
                    Save €{YEARLY_SAVINGS_EUR}
                  </span>
                </Link>
              </div>
            </div>

            {/* Phone mockup — native React screens that auto-cycle every
                4 seconds (Dashboard → Calendar → Expenses). No browser
                screenshot, so no URL bar to clip. On mobile the mock
                sits below the copy at a sensible width. */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="relative" data-testid="hero-phone-mockup">
                {/* Bezel */}
                <div className="relative w-[260px] sm:w-[300px] h-[540px] sm:h-[620px] rounded-[2.4rem] bg-[#1A2225] dark:bg-[#080B0C] p-3 shadow-[0_30px_60px_-20px_rgba(15,21,23,0.35)] ring-1 ring-black/5">
                  {/* Notch */}
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#1A2225] dark:bg-[#080B0C] rounded-b-2xl z-10" />
                  {/* Screen — overflow-hidden keeps every screen tidy
                      inside the rounded bezel. Each screen is a tiny
                      native React mockup (no screenshot, no URL bar). */}
                  <div
                    className="w-full h-full rounded-[1.9rem] overflow-hidden bg-white dark:bg-[#0E1416] relative"
                    data-testid={`hero-phone-screen-${heroScreen}`}
                  >
                    <div
                      key={heroScreen}
                      className="absolute inset-0 animate-in fade-in duration-500 ease-out"
                    >
                      {HERO_SCREEN_RENDERERS[heroScreen]}
                    </div>
                  </div>
                </div>
                {/* Cycle indicator — three tiny pills below the phone
                    that mirror the active screen. Tapping one jumps the
                    cycle so a curious visitor can browse. */}
                <div
                  className="mt-4 flex items-center justify-center gap-1.5"
                  role="tablist"
                  aria-label="Cycle through example screens"
                  data-testid="hero-phone-indicators"
                >
                  {HERO_SCREENS.map((s, i) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setHeroIdx(i)}
                      role="tab"
                      aria-selected={heroIdx === i}
                      aria-label={`Show ${s} screen`}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        heroIdx === i
                          ? "w-6 bg-[#116B6E] dark:bg-[#3FB8BC]"
                          : "w-1.5 bg-[#C9D5D7] dark:bg-[#2A3539] hover:bg-[#A8B6BB]"
                      }`}
                      data-testid={`hero-phone-indicator-${s}`}
                    />
                  ))}
                </div>
                {/* Subtle teal halo behind the phone */}
                <div
                  aria-hidden="true"
                  className="absolute inset-0 -z-10 blur-3xl bg-[#1F9FA4]/20 dark:bg-[#1F9FA4]/15 rounded-full"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------ 2. TRUST BAR ----------------------- */}
      <section
        className="border-y border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#FAFCFC] dark:bg-[#0B1011]"
        data-testid="trust-bar"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-4">
            <TrustBadge icon={HeartHandshake} label="Proudly Irish Built" testid="trust-irish" />
            <TrustBadge icon={Lock} label="Privacy-first" testid="trust-privacy" />
            <TrustBadge icon={ScrollText} label="Court-ready records" testid="trust-court" />
          </div>
        </div>
      </section>

      {/* ------------------------------ 3. FEATURES ------------------------ */}
      <section
        id="features"
        className="bg-white dark:bg-[#0E1416]"
        data-testid="features-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="Everything in one place"
            title="A calmer way to manage co-parenting"
            intro="Five tools that work together to remove ambiguity and keep every parenting event on the record."
            center
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5 mt-10">
            <FeatureCard
              icon={Calendar}
              title="Shared calendar"
              desc="Stay aligned on schedules, events and handovers."
              testid="feature-calendar"
            />
            <FeatureCard
              icon={HandHelping}
              title="Contact Visits"
              desc="Plan and document every handover, supervised visit and school collection with a complete, secure audit trail."
              testid="feature-contact-visits"
            />
            <FeatureCard
              icon={ReceiptEuro}
              title="Expenses tracking"
              desc="Log, split and settle clearly and fairly."
              testid="feature-expenses"
            />
            <FeatureCard
              icon={MessageSquare}
              title="Secure messaging"
              desc="Keep communication in one organised place."
              testid="feature-messaging"
            />
            <FeatureCard
              icon={BookOpen}
              title="Journals & records"
              desc="Time-stamped notes and supporting records as it happens."
              testid="feature-journals"
            />
          </div>
        </div>
      </section>

      {/* ----------------- 4. DESIGNED FOR REAL LIFE ----------------------- */}
      <section
        className="bg-[#FAFCFC] dark:bg-[#0B1011] border-y border-[#E5EBEC] dark:border-[#1F2A2D]"
        data-testid="real-life-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="Designed for real life"
            title="Designed for how co-parenting actually works"
            intro="Every family is different. CoParent Console adapts without forcing one fits-all flow."
            center
          />
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 mt-10">
            <SituationRow
              icon={Users}
              title="Multiple children, different co-parents"
              desc="Manage each child separately with clear boundaries."
              testid="situation-multi"
            />
            <SituationRow
              icon={ArrowLeftRight}
              title="Clear handovers and contact visits"
              desc="Every handover, supervised visit and third-party facilitated contact recorded with neutral, time-stamped detail."
              testid="situation-handovers"
            />
            <SituationRow
              icon={Wallet}
              title="Transparent finances"
              desc="No ambiguity around who owes what."
              testid="situation-finances"
            />
            <SituationRow
              icon={ShieldCheck}
              title="Reduced conflict"
              desc="Record facts, not opinions. Disputes preserve both records side-by-side &mdash; the platform never decides."
              testid="situation-conflict"
            />
          </div>
        </div>
      </section>

      {/* ----------------- 4c. TESTIMONIALS / SOCIAL PROOF ----------------- */}
      <section
        className="bg-[#FAFCFC] dark:bg-[#0B1011] border-y border-[#E5EBEC] dark:border-[#1F2A2D]"
        data-testid="testimonials-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="What people are saying"
            title="Calmer co-parenting, in their own words"
            intro="A small selection of stories from parents and the professionals who work alongside them. Names and details have been changed to protect privacy."
            center
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-12" data-testid="testimonials-grid">
            {[
              {
                quote: "Handovers used to be a daily argument. Two months in, we just check the calendar and get on with it. The kids feel the difference.",
                name: "Aoife",
                role: "Mum of two · Galway",
              },
              {
                quote: "The fingerprint on the export is the part my clients love most. Their solicitor can verify nothing's been edited — that ends a lot of arguments before they start.",
                name: "Family Mediator",
                role: "Dublin, Ireland",
              },
              {
                quote: "Splitting school costs used to be a row a month. We log it, the app calculates the share, and one of us settles it. Done. No spreadsheet, no guesswork.",
                name: "David",
                role: "Dad of three · Cork",
              },
              {
                quote: "I recommend it to clients in the early stages of separation. Having a calm, neutral record from day one saves them — and the court — a lot of time later.",
                name: "Family Law Solicitor",
                role: "Limerick, Ireland",
              },
              {
                quote: "I keep a private journal alongside our shared one. Knowing my notes are time-stamped and only mine has helped me feel less anxious about gaslighting.",
                name: "Sinéad",
                role: "Mum of one · Belfast",
              },
              {
                quote: "Read-only access for me as the GAL means I can review the schedule, communications and expenses without disturbing either parent. It's the cleanest setup I've worked with.",
                name: "Guardian ad Litem",
                role: "Ireland",
              },
            ].map((t, i) => (
              <figure
                key={t.name}
                className="relative border border-[#E5EBEC] dark:border-[#2A3539] bg-white dark:bg-[#161E21] rounded-xl p-6 shadow-[0_1px_2px_rgba(15,21,23,0.04)] hover:shadow-[0_8px_24px_-12px_rgba(31,159,164,0.18)] transition-shadow duration-200 flex flex-col"
                data-testid={`testimonial-${i}`}
              >
                {/* Quote glyph in the corner — a subtle accent that
                    earns its keep without competing with the stars. */}
                <Quote
                  aria-hidden="true"
                  className="absolute top-5 right-5 w-7 h-7 text-[#1F9FA4]/15 dark:text-[#1F9FA4]/25"
                  strokeWidth={1.4}
                />
                <div className="flex items-center gap-0.5" aria-label="Rated 5 out of 5">
                  {Array.from({ length: 5 }).map((_, idx) => (
                    <Star
                      key={`star-${t.name}-${idx}`}
                      className="w-4 h-4 text-[#F5A623] fill-[#F5A623]"
                      strokeWidth={1.4}
                    />
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-relaxed text-[#1A2225] dark:text-[#E8EEF0] flex-1">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <figcaption className="mt-5 pt-4 border-t border-[#EEF3F4] dark:border-[#2A3539]">
                  <div className="font-heading font-semibold text-sm text-[#1A2225] dark:text-[#F4F8F9]">
                    {t.name}
                  </div>
                  <div className="text-xs text-[#48595E] dark:text-[#A8B6BB] mt-0.5">
                    {t.role}
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>

          <p className="mt-10 text-center text-xs text-[#6F828A] dark:text-[#7A8A91] max-w-2xl mx-auto leading-relaxed">
            Verbatim feedback collected during the closed beta in Ireland (2025–2026). Names changed and minor edits made for clarity.
          </p>
        </div>
      </section>

      {/* ----------------- 5. LEGAL / ACCOUNTABILITY ----------------------- */}
      <section
        className="relative bg-white dark:bg-[#0E1416] overflow-hidden"
        data-testid="legal-section"
      >
        <div
          aria-hidden="true"
          className="absolute -top-20 right-0 w-[24rem] h-[24rem] rounded-full bg-[#1F9FA4]/8 dark:bg-[#1F9FA4]/10 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Accountability"
                title="Records you can rely on when it matters"
                intro="Every export carries a unique cryptographic fingerprint and a verification QR code, so anyone — a mediator, solicitor, or court — can confirm in seconds that the document hasn't been altered."
              />
              {/* Mock document preview — clean, professional, no PII.
                  The footer carries a real SHA-256 fingerprint mockup
                  and a "Verified · Tamper-evident" pill so the integrity
                  story is visible at a glance, not buried in copy. */}
              <div
                className="mt-8 max-w-sm rounded-lg overflow-hidden shadow-[0_18px_36px_-18px_rgba(15,21,23,0.25)] border border-[#E5EBEC] dark:border-[#2A3539] bg-white dark:bg-[#161E21] relative"
                data-testid="court-pdf-preview"
              >
                {/* "Verified" diagonal stamp — small, top-right corner;
                    teal and quietly authoritative rather than red/loud. */}
                <div
                  aria-hidden="true"
                  className="absolute top-3 right-3 z-10 px-2 py-1 rounded-md bg-[#1F9FA4]/10 dark:bg-[#1F9FA4]/20 border border-[#1F9FA4]/40 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#0B4D50] dark:text-[#3FB8BC] inline-flex items-center gap-1"
                >
                  <CheckCircle2 className="w-3 h-3" strokeWidth={2.4} />
                  Verified
                </div>
                <div className="px-5 py-4 border-b border-[#EEF3F4] dark:border-[#2A3539] bg-[#F4FAFA] dark:bg-[#0F2F30]/30">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[#116B6E] dark:text-[#3FB8BC] font-semibold">
                    Court Bundle · Apr 2026
                  </div>
                  <div className="font-heading font-semibold text-sm mt-1 text-[#1A2225] dark:text-[#F4F8F9]">
                    Expenses &amp; settlements — Child 1
                  </div>
                </div>
                <div className="px-5 py-4 space-y-3">
                  {[
                    ["Tutor", "€80.00", "Settled"],
                    ["School trip", "€42.00", "Outstanding"],
                    ["Optician", "€60.00", "Settled"],
                  ].map(([label, amt, status]) => (
                    <div
                      key={label}
                      className="flex items-center justify-between text-xs"
                    >
                      <div className="flex items-center gap-2">
                        <CircleDot className="w-3 h-3 text-[#1F9FA4]" />
                        <span className="text-[#1A2225] dark:text-[#E8EEF0]">{label}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono-data text-[#48595E] dark:text-[#A8B6BB]">{amt}</span>
                        <span
                          className={
                            status === "Settled"
                              ? "text-[10px] px-2 py-0.5 rounded-full bg-[#E0F2F2] text-[#116B6E] dark:bg-[#1F9FA4]/15 dark:text-[#3FB8BC]"
                              : "text-[10px] px-2 py-0.5 rounded-full bg-[#FFF1E5] text-[#A04E1B] dark:bg-[#3A2418] dark:text-[#FFB680]"
                          }
                        >
                          {status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Document fingerprint footer — the real product writes
                    this on every page of every export. We surface it here
                    so the integrity story is the LAST thing the eye
                    settles on after scanning the records list. */}
                <div className="px-5 pt-3 pb-4 border-t border-[#EEF3F4] dark:border-[#2A3539] bg-[#FBFDFD] dark:bg-[#0F2F30]/20">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#116B6E] dark:text-[#3FB8BC]">
                    <Fingerprint className="w-3.5 h-3.5" strokeWidth={2} />
                    Document fingerprint
                  </div>
                  <div className="flex items-start gap-3 mt-2">
                    <div
                      className="font-mono-data text-[10px] leading-snug text-[#48595E] dark:text-[#A8B6BB] break-all flex-1 select-all"
                      data-testid="court-pdf-fingerprint"
                    >
                      sha256:&nbsp;9f3a&middot;1c47&middot;b820&middot;e0d4
                      <br />
                      4a2f&middot;5e91&middot;c33b&middot;76aa
                      <br />
                      8d50&middot;1bef&middot;924c&middot;3f17
                    </div>
                    {/* Tiny QR mock — actual exports include a real
                        scannable QR pointing at /api/exports/verify/{hash}. */}
                    <div
                      aria-hidden="true"
                      className="w-12 h-12 rounded-md bg-white dark:bg-[#0E1416] border border-[#E5EBEC] dark:border-[#2A3539] inline-flex items-center justify-center flex-shrink-0"
                    >
                      <QrCode className="w-9 h-9 text-[#1A2225] dark:text-[#E8EEF0]" strokeWidth={1.6} />
                    </div>
                  </div>
                  <div className="mt-2 text-[10px] text-[#6F828A] dark:text-[#7A8A91]">
                    Scan or visit <span className="font-mono-data">app.coparentconsole.com/verify</span> to confirm this document is unaltered.
                  </div>
                </div>
                <div className="px-5 py-2.5 border-t border-[#EEF3F4] dark:border-[#2A3539] flex items-center justify-between text-[10px] text-[#6F828A]">
                  <span className="inline-flex items-center gap-1">
                    <Logo size={12} />
                    CoParent Console
                  </span>
                  <span>Page 4 of 12</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 space-y-4 sm:space-y-5">
              {/* Lead with the fingerprint claim — it's the strongest
                  differentiator in this section. The icon and copy mirror
                  the mockup so the eye threads naturally between them. */}
              <SituationRow
                icon={Fingerprint}
                title="Cryptographic document fingerprint"
                desc="Every page carries a SHA-256 hash. Anyone can verify in seconds that the file hasn't been changed."
                testid="legal-fingerprint"
              />
              <SituationRow
                icon={QrCode}
                title="Scan-to-verify QR code"
                desc="A QR on every export links to a public verification page — no account, no install, just confirmation."
                testid="legal-qr-verify"
              />
              <SituationRow
                icon={ScrollText}
                title="Court-ready exports"
                desc="Generate structured PDF bundles instantly."
                testid="legal-court-exports"
              />
              <SituationRow
                icon={FileLock2}
                title="Tamper-evident audit trail"
                desc="Immutable, time-stamped record of every action."
                testid="legal-tamper-evident"
              />
              <SituationRow
                icon={Users}
                title="Child-specific organisation"
                desc="Everything grouped per child for clarity."
                testid="legal-child-organised"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ------------------------------ 6. PRIVACY ------------------------- */}
      <section
        className="bg-[#FAFCFC] dark:bg-[#0B1011] border-y border-[#E5EBEC] dark:border-[#1F2A2D]"
        data-testid="privacy-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="Privacy & control"
            title="You decide what's shared, and what stays private"
            center
          />
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-5 mt-10">
            <FeatureCard
              icon={Eye}
              title="Per-child access control"
              desc="Only see what you are assigned to."
              testid="privacy-per-child"
            />
            <FeatureCard
              icon={Lock}
              title="Secure by design"
              desc="Built with privacy and data protection at its core."
              testid="privacy-secure"
            />
            <FeatureCard
              icon={GitBranch}
              title="Controlled sharing"
              desc="You choose what others can access."
              testid="privacy-sharing"
            />
          </div>
        </div>
      </section>

      {/* ------------------------------ 7. HOW IT WORKS -------------------- */}
      <section
        className="bg-white dark:bg-[#0E1416]"
        data-testid="how-it-works-section"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <SectionHeading
            eyebrow="Get started in minutes"
            title="How it works"
            center
          />
          <div className="mt-10 space-y-4">
            <StepCard
              num={1}
              icon={Users}
              title="Add your children"
              desc="Create a profile per child. The way you set things up follows their relationships."
              testid="step-add-children"
            />
            <StepCard
              num={2}
              icon={HeartHandshake}
              title="Invite a co-parent per child"
              desc="Each child can have its own co-parent. Boundaries stay clean and enforceable."
              testid="step-invite"
            />
            <StepCard
              num={3}
              icon={Calendar}
              title="Start managing everything in one place"
              desc="Schedules, contact visits, expenses, messages and records &mdash; all on the record from day one."
              testid="step-manage"
            />
          </div>
        </div>
      </section>

      {/* ------------------------------ 8. IRISH POSITIONING --------------- */}
      <section
        className="bg-gradient-to-b from-[#F4FAFA] to-white dark:from-[#0F2F30]/30 dark:to-[#0E1416] border-y border-[#E5EBEC] dark:border-[#1F2A2D]"
        data-testid="irish-section"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white dark:bg-[#161E21] border border-[#D6F0EF] dark:border-[#1F9FA4]/30">
            <HeartHandshake className="w-5 h-5 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.6} />
          </div>
          <h3 className="font-heading text-xl sm:text-2xl font-semibold mt-4 text-[#1A2225] dark:text-[#F4F8F9]">
            Proudly Irish Built
          </h3>
          <p className="text-sm sm:text-base leading-relaxed mt-2 text-[#48595E] dark:text-[#A8B6BB] max-w-xl mx-auto">
            Designed and developed in Ireland, with families in mind.
          </p>
        </div>
      </section>

      {/* ------------------------------ 9. FINAL CTA ----------------------- */}
      <section
        className="relative bg-white dark:bg-[#0E1416] overflow-hidden"
        data-testid="final-cta-section"
      >
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#1F9FA4]/10 dark:bg-[#1F9FA4]/15 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.1] text-[#1A2225] dark:text-[#F4F8F9]">
            Start using CoParent Console today
          </h2>
          <p className="text-base sm:text-lg leading-relaxed mt-5 max-w-xl mx-auto text-[#48595E] dark:text-[#A8B6BB]">
            Free to create. Built for clarity. Designed for accountability.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
            <Link
              to="/auth?mode=register"
              data-testid="final-cta-create"
              className="w-full sm:w-auto"
            >
              <Button className="w-full sm:w-auto bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-12 px-8 text-base font-medium shadow-sm transition-colors">
                Create account
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={2} />
              </Button>
            </Link>
            <Link
              to="/auth"
              data-testid="final-cta-login"
              className="w-full sm:w-auto"
            >
              <Button
                variant="outline"
                className="w-full sm:w-auto h-12 px-8 text-base rounded-md border-[#D6F0EF] dark:border-[#2A3539] bg-white dark:bg-[#161E21] text-[#1A2225] dark:text-[#E8EEF0] hover:border-[#116B6E] dark:hover:border-[#3FB8BC] hover:text-[#116B6E] dark:hover:text-[#3FB8BC]"
              >
                Log in
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ------------------------------ FOOTER ----------------------------- */}
      <footer
        className="border-t border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#FAFCFC] dark:bg-[#0B1011]"
        data-testid="landing-footer"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Logo size={24} />
            <span className="font-heading font-semibold text-sm text-[#1A2225] dark:text-[#F4F8F9]">
              CoParent Console
            </span>
          </div>
          <p className="text-xs text-[#6F828A] dark:text-[#7A8A91] text-center">
            Proudly Irish-made and Irish-owned. Built with privacy, trust, and families in mind.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-xs text-[#6F828A] dark:text-[#7A8A91]">
            <Link to="/about" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]" data-testid="footer-about-link">About</Link>
            <span aria-hidden="true">·</span>
            <Link to="/professionals" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]" data-testid="footer-professionals-link">For professionals</Link>
            <span aria-hidden="true">·</span>
            <Link to="/pricing" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]" data-testid="footer-pricing-link">Pricing</Link>
            <span aria-hidden="true">·</span>
            <Link to="/help" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]" data-testid="footer-help-link">Help</Link>
            <span aria-hidden="true">·</span>
            <Link to="/contact" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]" data-testid="footer-contact-link">Contact</Link>
            <span aria-hidden="true">·</span>
            <Link to="/privacy" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Privacy</Link>
            <span aria-hidden="true">·</span>
            <Link to="/terms" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]">Terms</Link>
            <span aria-hidden="true">·</span>
            <Link to="/security" className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC]" data-testid="footer-security-link">Security</Link>
          </div>
        </div>
        <LegalFooterInfo />
      </footer>
    </div>
  );
}
